import React from 'react';
import { Box, Chip, Stack, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { CHIP_WRAP_SX, CODE_BOX_SX, CODE_PRE_SX } from '../layoutSx';

const OP_CLASS = {
  set_viz_type: 'PRESENTATION', set_title: 'PRESENTATION', set_sort: 'PRESENTATION', set_layout: 'PRESENTATION',
  set_axis: 'PRESENTATION', set_style: 'PRESENTATION', set_color_series: 'PRESENTATION', toggle_legend_default: 'PRESENTATION',
  set_control_value: 'FILTER', add_control: 'FILTER', remove_control: 'FILTER',
  change_metric: 'DATA', change_grain: 'DATA', add_dimension: 'DATA', remove_dimension: 'DATA',
  add_widget_filter: 'DATA', change_aggregation: 'DATA',
  add_widget: 'STRUCTURE', remove_widget: 'STRUCTURE', duplicate_widget: 'STRUCTURE',
  explain_widget: 'META', explain_value: 'META',
};
const CLASS_COLOR = { PRESENTATION: 'default', FILTER: 'info', DATA: 'warning', STRUCTURE: 'secondary', META: 'default' };

const formatArgs = (args) => {
  if (!args || typeof args !== 'object') return '';
  return Object.entries(args)
    .map(([k, v]) => `${k}: ${typeof v === 'object' ? JSON.stringify(v) : v}`)
    .join(', ');
};

const SqlBox = ({ label, sql }) => (
  <Box sx={{ flex: 1, minWidth: 0 }}>
    <Typography variant="caption" sx={{ fontWeight: 600, color: 'text.secondary' }}>{label}</Typography>
    <Box sx={{ ...CODE_BOX_SX, mt: 0.25 }}>
      <Typography variant="caption" component="pre" sx={CODE_PRE_SX}>{sql}</Typography>
    </Box>
  </Box>
);

/**
 * Body of the EDIT_PLAN checkpoint (stageOutputJson = EditPlanPreviewDto, contracts §5.2):
 * the planned EditOps, affected widgets and, per widget, the binding diff with before/after SQL.
 */
export default function EditPlanDiff({ preview }) {
  const { t } = useTranslation();
  if (!preview) return null;
  const ops = Array.isArray(preview.ops) ? preview.ops : [];
  const diffs = Array.isArray(preview.widget_diffs) ? preview.widget_diffs : [];
  const affected = Array.isArray(preview.affected_widget_ids) ? preview.affected_widget_ids : [];
  const escalations = Array.isArray(preview.escalations) ? preview.escalations : [];
  const titleOf = (wid) => {
    const d = diffs.find((x) => x.widget_id === wid);
    return (d && d.title) || wid;
  };

  return (
    <Box sx={{ mb: 2, minWidth: 0, maxWidth: '100%', overflowWrap: 'anywhere' }}>
      <Typography variant="body2" sx={{ color: 'text.secondary', mb: 1 }}>{t('Dashboards.hitl.editPlanIntro')}</Typography>
      {preview.user_input && (
        <Box sx={{ borderLeft: '3px solid #c5b8ee', pl: 1.5, mb: 1.5 }}>
          <Typography variant="body2" sx={{ fontStyle: 'italic' }}>"{preview.user_input}"</Typography>
        </Box>
      )}

      <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 0.5 }}>{t('Dashboards.diff.opsTitle')}</Typography>
      <Stack spacing={0.5} sx={{ mb: 1.5 }}>
        {ops.map((o, i) => {
          const cls = OP_CLASS[o.op];
          return (
            <Box key={i} sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
              {cls && <Chip size="small" color={CLASS_COLOR[cls]} variant="outlined" sx={CHIP_WRAP_SX} label={t(`Dashboards.diff.opClass.${cls}`)} />}
              <Typography variant="body2" sx={{ fontWeight: 600, minWidth: 0 }}>{t(`Dashboards.diff.ops.${o.op}`, { defaultValue: o.op })}</Typography>
              {(o.target_widget_id || o.control_id) && (
                <Typography variant="caption" color="text.secondary" sx={{ minWidth: 0 }}>→ {o.target_widget_id ? titleOf(o.target_widget_id) : o.control_id}</Typography>
              )}
              {formatArgs(o.args) && (
                <Typography variant="caption" sx={{ fontFamily: 'monospace', color: 'text.secondary', minWidth: 0, maxWidth: '100%' }}>({formatArgs(o.args)})</Typography>
              )}
            </Box>
          );
        })}
      </Stack>

      {affected.length > 0 && (
        <Box sx={{ mb: 1.5 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 0.5 }}>{t('Dashboards.diff.affectedWidgets')}</Typography>
          <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mb: 0.5 }}>
            {affected.map((wid) => <Chip key={wid} size="small" color="primary" variant="outlined" sx={CHIP_WRAP_SX} label={titleOf(wid)} />)}
          </Box>
          <Typography variant="caption" color="text.secondary">{t('Dashboards.diff.unaffected')}</Typography>
        </Box>
      )}

      {escalations.map((e, i) => {
        const wid = typeof e === 'string' ? e : (e && (e.widget_id || e.target_widget_id));
        return (
          <Box key={`esc-${i}`} sx={{ mb: 1, p: 1, backgroundColor: '#fff8e1', borderRadius: 1, border: '1px solid #ffe082' }}>
            <Typography variant="caption" sx={{ color: 'warning.dark' }}>
              {t('Dashboards.diff.escalated', { widget: titleOf(wid) })}
              {e && typeof e === 'object' && e.reason ? ` ${e.reason}` : ''}
            </Typography>
          </Box>
        );
      })}

      {diffs.map((d) => {
        const bd = d.binding_diff;
        const flags = bd ? [
          ['predicates_changed', 'predicatesChanged'], ['group_by_changed', 'groupByChanged'],
          ['aggregations_changed', 'aggregationsChanged'], ['parameters_changed', 'parametersChanged'],
        ].filter(([k]) => bd[k]) : [];
        const added = (bd && bd.tables_added) || [];
        const removed = (bd && bd.tables_removed) || [];
        const details = (bd && bd.details) || [];
        const sqlChanged = d.after_sql && d.after_sql !== d.before_sql;
        return (
          <Box key={d.widget_id} sx={{ border: '1px solid #e0e0e0', borderRadius: 2, p: 1.25, mb: 1, bgcolor: '#fff', minWidth: 0 }}>
            <Typography variant="body2" sx={{ fontWeight: 600 }}>{d.title || d.widget_id}</Typography>
            {d.summary && <Typography variant="caption" color="text.secondary" display="block">{d.summary}</Typography>}
            {(flags.length > 0 || added.length > 0 || removed.length > 0) && (
              <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', my: 0.75 }}>
                {flags.map(([k, key]) => <Chip key={k} size="small" color="warning" variant="outlined" sx={CHIP_WRAP_SX} label={t(`Dashboards.diff.${key}`)} />)}
                {added.map((tb) => <Chip key={`a-${tb}`} size="small" color="success" variant="outlined" sx={CHIP_WRAP_SX} label={`${t('Dashboards.diff.tablesAdded')}: ${tb}`} />)}
                {removed.map((tb) => <Chip key={`r-${tb}`} size="small" color="error" variant="outlined" sx={CHIP_WRAP_SX} label={`${t('Dashboards.diff.tablesRemoved')}: ${tb}`} />)}
              </Box>
            )}
            {details.map((x, i) => (
              <Typography key={i} variant="caption" display="block" sx={{ fontFamily: 'monospace', pl: 1 }}>• {x}</Typography>
            ))}
            {d.before_sql && (
              <Box sx={{ display: 'flex', gap: 1, mt: 1, minWidth: 0, flexDirection: { xs: 'column', md: 'row' } }}>
                <SqlBox label={t('Dashboards.diff.before')} sql={d.before_sql} />
                {sqlChanged && <SqlBox label={t('Dashboards.diff.after')} sql={d.after_sql} />}
              </Box>
            )}
            {d.after_sql && !sqlChanged && (
              <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>{t('Dashboards.diff.noSqlChange')}</Typography>
            )}
          </Box>
        );
      })}
    </Box>
  );
}
